import React, { useState, useEffect, useRef, useCallback } from 'react';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, getDocs, doc, addDoc, updateDoc, deleteDoc, Timestamp, setDoc, query, orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// Services
import { auth, db, functions, FIREBASE_FUNCTIONS_URL, LEGACY_URL } from './services/firebase';

// Styles
import { styles } from './styles/theme';

// Utils
import { formatBRL, formatDateBR } from './utils/formatters';
import { parseLocalDate } from './utils/dateUtils';
import { exportContractsToCSV, exportRevenueToCSV } from './utils/exportUtils';

// Hooks
import useDebounce from './hooks/useDebounce';

// Components
import {
  ErrorBoundary,
  ToastProvider,
  useToast,
  Modal,
  ConfirmDialog,
  TableRowSkeleton,
  StatCardSkeleton,
  StatusBadge,
  Pagination,
  TemplateVariablesHelp,
  EmailPreview
} from './components/common';

// Main App wrapped with providers
function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <EmailAdminApp />
      </ToastProvider>
    </ErrorBoundary>
  );
}

function EmailAdminApp() {
  const toast = useToast();

  // Auth state - NO DEFAULT CREDENTIALS
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Data state
  const [contracts, setContracts] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [revenueForecast, setRevenueForecast] = useState({});
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [emailsByContract, setEmailsByContract] = useState({});
  const [revenueProjection, setRevenueProjection] = useState({});
  const [customEmails, setCustomEmails] = useState({});

  // Parcels loaded once and shared across reports
  const [parcelsByContract, setParcelsByContract] = useState({});
  const [contractStats, setContractStats] = useState({});
  const [receivedByMonth, setReceivedByMonth] = useState({});
  const [mrr, setMrr] = useState(0);
  const [revenueView, setRevenueView] = useState('expected'); // 'expected' | 'received'
  const [revenueTableView, setRevenueTableView] = useState('chart'); // 'chart' | 'table' for received
  const [selectedYear, setSelectedYear] = useState(null); // filter for previsto view
  const [selectedMonth, setSelectedMonth] = useState(null); // filter for previsto view
  const [hoveredBar, setHoveredBar] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    clientName: '',
    email: '',
    product: '',
    contractType: 'installment',
    totalValue: '',
    monthlyValue: '',
    numParcels: 12,
    startDate: '',
    status: 'active' // Contract status field
  });
  const [editingId, setEditingId] = useState(null);

  // UI state
  const [currentTab, setCurrentTab] = useState('dashboard');
  const [selectedContract, setSelectedContract] = useState(null);
  const [editingEmail, setEditingEmail] = useState(null);
  const [showNewContractModal, setShowNewContractModal] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(null);
  const [showContractDetail, setShowContractDetail] = useState(null);
  const [contractParcels, setContractParcels] = useState([]);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Search and filter state
  const [contractsSearch, setContractsSearch] = useState('');
  const debouncedSearch = useDebounce(contractsSearch, 300);
  const [contractsFilter, setContractsFilter] = useState('all');
  const [contractsSort, setContractsSort] = useState('date-desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Loading state
  const [loading, setLoading] = useState({
    contracts: false,
    projections: false,
    emails: false,
    submitting: false
  });

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState({
    welcome: '',
    reminder: '',
    payment: '',
    followup: '',
    welcome_recurring: '',
    reminder_recurring: '',
    payment_recurring: '',
    followup_recurring: ''
  });
  const [emailSubjects, setEmailSubjects] = useState({
    welcome: '🎉 Contrato Confirmado - Bem-vindo!',
    reminder: 'Lembrete: Parcela {parcela_num} vence em 7 dias',
    payment: 'Parcela {parcela_num} vence hoje',
    followup: 'Parcela {parcela_num} em atraso',
    welcome_recurring: '🎉 Serviço Confirmado - Bem-vindo!',
    reminder_recurring: 'Lembrete: Seu serviço {produto} vence em 7 dias',
    payment_recurring: 'Pagamento recorrente vence hoje',
    followup_recurring: 'Pagamento recorrente em atraso'
  });

  // Auth listener
  useEffect(() => {
    onAuthStateChanged(auth, setUser);
  }, []);

  // Load data when user logs in
  useEffect(() => {
    if (user) {
      loadEmailTemplates();
      fetchContracts();
      loadCustomEmails();
    }
  }, [user]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, contractsFilter, contractsSort]);

  // Auth functions
  const login = async () => {
    if (!email || !password) {
      toast.error('Por favor, preencha email e senha');
      return;
    }
    try {
      await signInWithEmailAndPassword(auth, email, password);
      toast.success('Login realizado com sucesso!');
    } catch (error) {
      toast.error('Erro de login: ' + error.message);
    }
  };

  const logout = async () => {
    await signOut(auth);
    toast.info('Voce saiu do sistema');
  };

  // Email content generation
  const generateEmailContent = useCallback((emailItem, contract) => {
    const templateMap = {
      'welcome': emailTemplates.welcome,
      'reminder': emailTemplates.reminder,
      'payment': emailTemplates.payment,
      'followup': emailTemplates.followup
    };

    const template = templateMap[emailItem.type] || '';

    return template
      .replace(/{nome}/g, contract.clientName)
      .replace(/{produto}/g, contract.product)
      .replace(/{valor_total}/g, formatBRL(contract.totalValue))
      .replace(/{num_parcelas}/g, contract.numParcels)
      .replace(/{total_parcelas}/g, contract.numParcels)
      .replace(/{parcela_num}/g, emailItem.parcelNum || 1)
      .replace(/{valor_parcela}/g, emailItem.value ? formatBRL(emailItem.value) : formatBRL(contract.totalValue / contract.numParcels))
      .replace(/{data_vencimento}/g, emailItem.dueDate ? emailItem.dueDate.toLocaleDateString('pt-BR') : '')
      .replace(/{data_primeira_parcela}/g, contract.startDate?.toDate().toLocaleDateString('pt-BR') || '');
  }, [emailTemplates]);

  const generateEmailSubject = useCallback((emailItem, contract) => {
    const subjectTemplate = emailSubjects[emailItem.type] || '';

    return subjectTemplate
      .replace(/{nome}/g, contract?.clientName || '')
      .replace(/{produto}/g, contract?.product || '')
      .replace(/{parcela_num}/g, emailItem?.parcelNum || 1)
      .replace(/{total_parcelas}/g, contract?.numParcels || 1);
  }, [emailSubjects]);

  const getEmailKey = (contractId, type, parcelNum = null) => {
    return `${contractId}-${type}${parcelNum ? `-${parcelNum}` : ''}`;
  };

  const getIndividualEmailContent = useCallback((emailItem, contract) => {
    const emailKey = getEmailKey(emailItem.contractId, emailItem.type, emailItem.parcelNum);
    if (customEmails[emailKey]) {
      return customEmails[emailKey];
    }
    return generateEmailContent(emailItem, contract);
  }, [customEmails, generateEmailContent]);

  // Contract status helper - derived from real parcel data
  const getContractStatus = useCallback((contract) => {
    // Check for explicit status first
    if (contract.status === 'cancelled') return 'cancelled';
    if (contract.status === 'draft') return 'draft';

    const stats = contractStats[contract.id];
    if (!stats) return 'active';

    if (stats.overdueAmount > 0) return 'overdue';
    if (contract.contractType !== 'recurring' && stats.total > 0 && stats.paid >= stats.total) {
      return 'completed';
    }
    return 'active';
  }, [contractStats]);

  // Filter and sort contracts with pagination
  const getFilteredAndSortedContracts = useCallback(() => {
    let filtered = contracts.filter(contract => {
      // Search filter (using debounced value)
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        const matches =
          contract.clientName.toLowerCase().includes(searchLower) ||
          contract.email.toLowerCase().includes(searchLower) ||
          contract.product.toLowerCase().includes(searchLower);
        if (!matches) return false;
      }

      // Status filter
      if (contractsFilter !== 'all') {
        const status = getContractStatus(contract);
        if (contractsFilter !== status) return false;
      }

      return true;
    });

    // Sort contracts
    filtered.sort((a, b) => {
      switch (contractsSort) {
        case 'date-asc':
          return new Date(a.startDate) - new Date(b.startDate);
        case 'date-desc':
          return new Date(b.startDate) - new Date(a.startDate);
        case 'value-asc':
          return a.totalValue - b.totalValue;
        case 'value-desc':
          return b.totalValue - a.totalValue;
        case 'name-asc':
          return a.clientName.localeCompare(b.clientName);
        default:
          return new Date(b.startDate) - new Date(a.startDate);
      }
    });

    return filtered;
  }, [contracts, debouncedSearch, contractsFilter, contractsSort, getContractStatus]);

  // Paginated contracts
  const getPaginatedContracts = useCallback(() => {
    const filtered = getFilteredAndSortedContracts();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return {
      contracts: filtered.slice(startIndex, endIndex),
      totalItems: filtered.length,
      totalPages: Math.ceil(filtered.length / itemsPerPage)
    };
  }, [getFilteredAndSortedContracts, currentPage, itemsPerPage]);

  // Load every contract's parcels once, keyed by contract id
  const loadAllParcels = async (contractsData) => {
    const entries = await Promise.all(
      contractsData.map(async (contract) => {
        try {
          const snap = await getDocs(collection(db, 'contracts', contract.id, 'parcels'));
          return [contract.id, snap.docs.map(d => d.data())];
        } catch (error) {
          return [contract.id, []];
        }
      })
    );
    return Object.fromEntries(entries);
  };

  // Derive per-contract paid/pending/overdue stats from real parcel data
  const computeContractStats = (contractsData, parcelsMap) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const stats = {};
    contractsData.forEach(contract => {
      const parcels = parcelsMap[contract.id] || [];
      let paid = 0;
      let paidAmount = 0;
      let pendingAmount = 0;
      let overdueAmount = 0;

      parcels.forEach(parcel => {
        const value = parcel.value || 0;
        if (parcel.status === 'paid') {
          paid += 1;
          paidAmount = Math.round((paidAmount + value) * 100) / 100;
        } else {
          const dueDate = parcel.dueDate ? parcel.dueDate.toDate() : null;
          if (dueDate && dueDate < today) {
            overdueAmount = Math.round((overdueAmount + value) * 100) / 100;
          } else {
            pendingAmount = Math.round((pendingAmount + value) * 100) / 100;
          }
        }
      });

      stats[contract.id] = {
        paid,
        total: parcels.length || contract.numParcels || 0,
        paidAmount,
        pendingAmount,
        overdueAmount,
      };
    });

    setContractStats(stats);
  };

  // Data fetching functions
  const fetchContracts = async () => {
    setLoading(prev => ({ ...prev, contracts: true }));

    try {
      const snapshot = await getDocs(collection(db, 'contracts'));
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setContracts(data);

      // Total contracted: installment contracts only (recurring has no fixed total).
      // Cancelled contracts count only what was actually paid; that portion is
      // added back below once parcels load, since it needs the parcel data.
      const installmentTotal = data
        .filter(c => c.contractType !== 'recurring' && c.status !== 'cancelled')
        .reduce((sum, c) => Math.round((sum + (c.totalValue || 0)) * 100) / 100, 0);
      setTotalValue(installmentTotal);

      // MRR: active recurring contracts
      const monthlyRecurring = data
        .filter(c => c.contractType === 'recurring' && c.status !== 'cancelled')
        .reduce((sum, c) => Math.round((sum + (c.monthlyValue || 0)) * 100) / 100, 0);
      setMrr(monthlyRecurring);

      setLoading(prev => ({ ...prev, contracts: false }));

      // Load parcels ONCE, then derive everything from the shared map
      const parcelsMap = await loadAllParcels(data);
      setParcelsByContract(parcelsMap);

      // Cancelled installment contracts contribute only their collected amount
      const cancelledPaid = data
        .filter(c => c.contractType !== 'recurring' && c.status === 'cancelled')
        .reduce((sum, c) => {
          const paid = (parcelsMap[c.id] || [])
            .filter(p => p.status === 'paid')
            .reduce((s, p) => s + (p.value || 0), 0);
          return Math.round((sum + paid) * 100) / 100;
        }, 0);
      setTotalValue(Math.round((installmentTotal + cancelledPaid) * 100) / 100);
      computeContractStats(data, parcelsMap);
      calculateRevenueProjection(data, parcelsMap);
      fetchScheduledEmails(data, parcelsMap);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('Erro ao carregar contratos');
      setLoading(prev => ({ ...prev, contracts: false }));
    }
  };

  const calculateRevenueProjection = (contractsData, parcelsMap) => {
    setLoading(prev => ({ ...prev, projections: true }));

    try {
      const projection = {};
      const received = {};
      const today = new Date();

      contractsData.forEach((contract) => {
        const parcels = parcelsMap[contract.id] || [];
        const isCancelled = contract.status === 'cancelled';
        parcels.forEach(parcel => {
          // Cancelled contracts: keep paid history, drop unpaid parcels from projections
          if (isCancelled && parcel.status !== 'paid') return;

          // Cash actually received, bucketed by payment date (paidAt)
          if (parcel.status === 'paid' && parcel.paidAt) {
            const paidDate = parcel.paidAt.toDate();
            const paidKey = `${paidDate.getFullYear()}-${String(paidDate.getMonth() + 1).padStart(2, '0')}`;
            const paidMonthName = paidDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
            if (!received[paidKey]) {
              received[paidKey] = { monthName: paidMonthName, received: 0, onTime: 0, late: 0 };
            }
            received[paidKey].received = Math.round((received[paidKey].received + parcel.value) * 100) / 100;
            const dueForParcel = parcel.dueDate ? parcel.dueDate.toDate() : null;
            if (dueForParcel && paidDate <= dueForParcel) {
              received[paidKey].onTime = Math.round((received[paidKey].onTime + parcel.value) * 100) / 100;
            } else {
              received[paidKey].late = Math.round((received[paidKey].late + parcel.value) * 100) / 100;
            }
          }

          if (parcel.dueDate) {
            const dueDate = parcel.dueDate.toDate();
            const monthKey = `${dueDate.getFullYear()}-${String(dueDate.getMonth() + 1).padStart(2, '0')}`;
            const monthName = dueDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

            if (!projection[monthKey]) {
              projection[monthKey] = {
                monthName,
                date: dueDate,
                total: 0,
                pending: 0,
                paid: 0,
                overdue: 0,
                contracts: []
              };
            }

            const contractInfo = {
              id: contract.id,
              clientName: contract.clientName,
              product: contract.product,
              parcelNum: parcel.parcelNum,
              value: parcel.value,
              status: parcel.status || 'pending',
              dueDate: dueDate
            };

            projection[monthKey].total = Math.round((projection[monthKey].total + parcel.value) * 100) / 100;
            projection[monthKey].contracts.push(contractInfo);

            const status = parcel.status || 'pending';
            if (status === 'pending') {
              if (dueDate < today) {
                projection[monthKey].overdue = Math.round((projection[monthKey].overdue + parcel.value) * 100) / 100;
              } else {
                projection[monthKey].pending = Math.round((projection[monthKey].pending + parcel.value) * 100) / 100;
              }
            } else if (status === 'paid') {
              projection[monthKey].paid = Math.round((projection[monthKey].paid + parcel.value) * 100) / 100;
            }
          }
        });
      });

      const forecast = {};
      Object.entries(projection).forEach(([monthKey, monthData]) => {
        if (monthData.pending > 0) {
          forecast[monthData.monthName] = monthData.pending;
        }
      });
      setRevenueForecast(forecast);

      const sortedProjection = Object.fromEntries(
        Object.entries(projection).sort(([a], [b]) => a.localeCompare(b))
      );

      setRevenueProjection(sortedProjection);

      const sortedReceived = Object.fromEntries(
        Object.entries(received).sort(([a], [b]) => a.localeCompare(b))
      );
      setReceivedByMonth(sortedReceived);

      setLoading(prev => ({ ...prev, projections: false }));
    } catch (error) {
      console.error('Error calculating revenue:', error);
      setLoading(prev => ({ ...prev, projections: false }));
    }
  };

  const fetchScheduledEmails = async (contractsData, parcelsMap) => {
    setLoading(prev => ({ ...prev, emails: true }));

    try {
      const emailsByContractMap = {};
      const allEmails = [];
      const today = new Date();

      const emailLogsSnapshot = await getDocs(collection(db, 'email_logs'));

      const sentEmails = new Set();
      emailLogsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.contractId && data.type === 'welcome') {
          sentEmails.add(data.contractId);
        }
      });

      contractsData.forEach((contract) => {
        const parcels = parcelsMap[contract.id] || [];
        const contractEmails = [];

        if (sentEmails.has(contract.id)) {
          contractEmails.push({
            type: 'welcome',
            contractId: contract.id,
            clientName: contract.clientName,
            email: contract.email,
            product: contract.product,
            scheduledDate: contract.createdAt?.toDate() || new Date(),
            subject: 'Contrato Confirmado - Bem-vindo!',
            status: 'sent'
          });
        }

        // Cancelled/draft contracts: no billing emails for pending parcels
        const billingStopped = contract.status === 'cancelled' || contract.status === 'draft';

        parcels.forEach(parcel => {
          if (parcel.status === 'pending' && !billingStopped) {
            const dueDate = parcel.dueDate.toDate();
            const reminderDate = new Date(dueDate);
            reminderDate.setDate(dueDate.getDate() - 7);

            if (reminderDate >= today) {
              contractEmails.push({
                type: 'reminder',
                contractId: contract.id,
                clientName: contract.clientName,
                email: contract.email,
                product: contract.product,
                parcelNum: parcel.parcelNum,
                value: parcel.value,
                dueDate: dueDate,
                scheduledDate: reminderDate,
                subject: `Lembrete: Parcela ${parcel.parcelNum} vence em 7 dias`,
                status: 'scheduled'
              });
            }

            contractEmails.push({
              type: 'payment',
              contractId: contract.id,
              clientName: contract.clientName,
              email: contract.email,
              product: contract.product,
              parcelNum: parcel.parcelNum,
              value: parcel.value,
              dueDate: dueDate,
              scheduledDate: dueDate,
              subject: `Parcela ${parcel.parcelNum} vence hoje`,
              status: 'scheduled'
            });

            const followupDate = new Date(dueDate);
            followupDate.setDate(dueDate.getDate() + 3);
            contractEmails.push({
              type: 'followup',
              contractId: contract.id,
              clientName: contract.clientName,
              email: contract.email,
              product: contract.product,
              parcelNum: parcel.parcelNum,
              value: parcel.value,
              dueDate: dueDate,
              scheduledDate: followupDate,
              subject: `Parcela ${parcel.parcelNum} em atraso`,
              status: 'scheduled'
            });
          }
        });

        contractEmails.sort((a, b) => a.scheduledDate - b.scheduledDate);
        emailsByContractMap[contract.id] = contractEmails;
        allEmails.push(...contractEmails);
      });

      allEmails.sort((a, b) => a.scheduledDate - b.scheduledDate);
      setScheduledEmails(allEmails);
      setEmailsByContract(emailsByContractMap);
      setLoading(prev => ({ ...prev, emails: false }));
    } catch (error) {
      console.error('Error fetching emails:', error);
      setLoading(prev => ({ ...prev, emails: false }));
    }
  };

  const loadEmailTemplates = async () => {
    const recurringDefaults = {
      welcome_recurring: `🎉 Parabéns! Seu serviço foi confirmado!

Olá {nome},

É com grande satisfação que confirmamos a contratação do seu serviço {produto}.

📋 **Detalhes do Serviço:**
• Serviço: {produto}
• Valor Mensal: R$ {valor_mensal}
• Data do Primeiro Pagamento: {data_primeira_parcela}
• Recorrência: Mensal

💳 **Forma de Pagamento:**
PIX: chave@exemplo.com

Você receberá lembretes automáticos antes de cada vencimento para facilitar o acompanhamento.

Obrigado pela confiança!

Atenciosamente,
Equipe Webinar Company`,
      reminder_recurring: `⏰ Seu serviço vence em breve!

Olá {nome},

Esperamos que esteja satisfeito com nosso serviço {produto}! Este é um lembrete amigável sobre sua próxima cobrança recorrente.

📋 **Detalhes do Pagamento:**
• Serviço: {produto}
• Valor Mensal: R$ {valor_mensal}
• Data de Vencimento: {data_vencimento}

💳 **Para pagamento via PIX:**
Chave: chave@exemplo.com

Caso já tenha efetuado o pagamento, pode desconsiderar este lembrete.

Obrigado pela confiança!

Atenciosamente,
Equipe Webinar Company`,
      payment_recurring: `💰 Pagamento recorrente vence hoje!

Olá {nome},

Sua cobrança recorrente de {produto} vence hoje! Que tal já resolver isso agora mesmo?

📋 **Pagamento de Hoje:**
• Serviço: {produto}
• Valor Mensal: R$ {valor_mensal}
• Vencimento: HOJE ({data_vencimento})

💳 **Pagamento via PIX:**
Chave: chave@exemplo.com

📞 Em caso de dúvidas, estamos à disposição!

Atenciosamente,
Equipe Webinar Company`,
      followup_recurring: `❗ Pagamento recorrente em atraso - Vamos regularizar?

Olá {nome},

Notamos que sua cobrança recorrente de {produto} está pendente há alguns dias. Que tal regularizarmos a situação?

📋 **Pagamento Pendente:**
• Serviço: {produto}
• Valor Mensal: R$ {valor_mensal}
• Venceu em: {data_vencimento}

💳 **Para quitar via PIX:**
Chave: chave@exemplo.com

Valorizamos muito nosso relacionamento com você. Estamos aqui para ajudar caso tenha alguma dificuldade!

Atenciosamente,
Equipe Webinar Company`
    };

    const recurringSubjectDefaults = {
      welcome_recurring: '🎉 Serviço Confirmado - Bem-vindo!',
      reminder_recurring: 'Lembrete: Seu serviço {produto} vence em 7 dias',
      payment_recurring: 'Pagamento recorrente vence hoje',
      followup_recurring: 'Pagamento recorrente em atraso'
    };

    try {
      const templatesQuery = query(collection(db, 'email_templates'), orderBy('updatedAt', 'desc'), limit(1));
      const templatesSnapshot = await getDocs(templatesQuery);
      if (!templatesSnapshot.empty) {
        const latestTemplate = templatesSnapshot.docs[0].data();
        if (latestTemplate.templates) {
          // Merge with recurring defaults so editors see them even if DB is from an older version
          setEmailTemplates({ ...recurringDefaults, ...latestTemplate.templates });
        }
        if (latestTemplate.subjects) {
          setEmailSubjects({ ...recurringSubjectDefaults, ...latestTemplate.subjects });
        }
        return;
      }

      // Default templates
      setEmailTemplates({
        welcome: `Parabens! Seu contrato foi confirmado!

Ola {nome},

E com grande satisfacao que confirmamos a criacao do seu contrato para {produto}.

Detalhes do Contrato:
- Produto/Servico: {produto}
- Valor Total: R$ {valor_total}
- Numero de Parcelas: {num_parcelas}
- Data da Primeira Parcela: {data_primeira_parcela}
- Valor da Parcela: R$ {valor_parcela}

Forma de Pagamento:
PIX: chave@exemplo.com

Voce recebera lembretes automaticos antes de cada vencimento.

Obrigado pela confianca!

Atenciosamente,
Equipe Webinar Company`,
        reminder: `Lembrete: Parcela vencendo em breve

Ola {nome},

Este e um lembrete amigavel sobre sua proxima parcela:

Detalhes da Parcela:
- Produto: {produto}
- Parcela: {parcela_num} de {total_parcelas}
- Valor: R$ {valor_parcela}
- Vencimento: {data_vencimento}

Para pagamento via PIX:
Chave: chave@exemplo.com

Caso ja tenha efetuado o pagamento, pode desconsiderar este lembrete.

Atenciosamente,
Equipe Webinar Company`,
        payment: `Parcela vence hoje - Nao esqueca!

Ola {nome},

Sua parcela vence hoje! Que tal ja resolver isso agora mesmo?

Parcela de Hoje:
- Produto: {produto}
- Parcela: {parcela_num} de {total_parcelas}
- Valor: R$ {valor_parcela}
- Vencimento: HOJE ({data_vencimento})

Pagamento via PIX:
Chave: chave@exemplo.com

Em caso de duvidas, estamos a disposicao!

Atenciosamente,
Equipe Webinar Company`,
        followup: `Parcela em atraso - Vamos regularizar?

Ola {nome},

Notamos que sua parcela esta pendente ha alguns dias. Que tal regularizarmos a situacao?

Parcela Pendente:
- Produto: {produto}
- Parcela: {parcela_num} de {total_parcelas}
- Valor: R$ {valor_parcela}
- Venceu em: {data_vencimento}

Para quitar via PIX:
Chave: chave@exemplo.com

Estamos aqui para ajudar caso tenha alguma dificuldade. Entre em contato!

Atenciosamente,
Equipe Webinar Company`,
        ...recurringDefaults
      });
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadCustomEmails = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'custom_emails'));
      const customEmailsData = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        customEmailsData[data.emailKey] = data.content;
      });
      setCustomEmails(customEmailsData);
    } catch (error) {
      console.error('Error loading custom emails:', error);
    }
  };

  const saveEmailTemplates = async () => {
    try {
      const oldTemplatesSnapshot = await getDocs(collection(db, 'email_templates'));
      const deletePromises = oldTemplatesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      await addDoc(collection(db, 'email_templates'), {
        templates: emailTemplates,
        subjects: emailSubjects,
        updatedAt: Timestamp.now(),
        updatedBy: user?.uid || 'system'
      });

      toast.success('Templates salvos com sucesso!');
    } catch (error) {
      console.error('Error saving templates:', error);
      toast.error('Erro ao salvar templates');
    }
  };

  // CRUD operations
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Voce deve estar logado');
      return;
    }

    setLoading(prev => ({ ...prev, submitting: true }));

    try {
      const isRecurring = formData.contractType === 'recurring';
      const parcelValue = isRecurring
        ? parseFloat(formData.monthlyValue)
        : Math.round((parseFloat(formData.totalValue) / parseInt(formData.numParcels)) * 100) / 100;
      const numParcels = isRecurring ? 12 : parseInt(formData.numParcels);

      if (editingId) {
        const contractRef = doc(db, 'contracts', editingId);
        const contractUpdateData = {
          clientName: formData.clientName,
          email: formData.email,
          product: formData.product,
          contractType: formData.contractType,
          startDate: Timestamp.fromDate(parseLocalDate(formData.startDate)),
          status: formData.status,
          updatedAt: Timestamp.now(),
        };

        if (isRecurring) {
          contractUpdateData.monthlyValue = parseFloat(formData.monthlyValue);
          contractUpdateData.totalValue = null;
        } else {
          contractUpdateData.totalValue = parseFloat(formData.totalValue);
          contractUpdateData.monthlyValue = null;
        }
        contractUpdateData.numParcels = numParcels;

        await updateDoc(contractRef, contractUpdateData);

        const start = parseLocalDate(formData.startDate);

        const parcelPromises = [];
        for (let i = 1; i <= numParcels; i++) {
          const dueDate = new Date(start.getFullYear(), start.getMonth() + (i - 1), start.getDate());
          if (dueDate.getDate() !== start.getDate()) {
            dueDate.setDate(0);
          }

          parcelPromises.push(
            setDoc(doc(db, 'contracts', editingId, 'parcels', i.toString()), {
              parcelNum: i,
              value: parcelValue,
              dueDate: Timestamp.fromDate(dueDate),
              status: 'pending',
              followUpSent: false,
            })
          );
        }

        await Promise.all(parcelPromises);
        toast.success('Contrato atualizado com sucesso!');
      } else {
        // Create contract in Firestore
        const contractData = {
          clientName: formData.clientName,
          email: formData.email,
          product: formData.product,
          contractType: formData.contractType,
          numParcels: numParcels,
          startDate: Timestamp.fromDate(parseLocalDate(formData.startDate)),
          status: formData.status || 'active',
          createdAt: Timestamp.now(),
          createdBy: user.uid,
        };

        if (isRecurring) {
          contractData.monthlyValue = parseFloat(formData.monthlyValue);
          contractData.totalValue = null;
        } else {
          contractData.totalValue = parseFloat(formData.totalValue);
          contractData.monthlyValue = null;
        }

        const contractRef = await addDoc(collection(db, 'contracts'), contractData);

        const start = parseLocalDate(formData.startDate);

        const parcelPromises = [];
        for (let i = 1; i <= numParcels; i++) {
          const dueDate = new Date(start.getFullYear(), start.getMonth() + (i - 1), start.getDate());
          if (dueDate.getDate() !== start.getDate()) {
            dueDate.setDate(0);
          }

          parcelPromises.push(
            setDoc(doc(db, 'contracts', contractRef.id, 'parcels', i.toString()), {
              parcelNum: i,
              value: parcelValue,
              dueDate: Timestamp.fromDate(dueDate),
              status: 'pending',
              followUpSent: false,
            })
          );
        }

        await Promise.all(parcelPromises);

        // Send welcome email via HTTP function
        try {
          const welcomeUrl = `${FIREBASE_FUNCTIONS_URL}/sendWelcomeEmail`;
          await fetch(welcomeUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: formData.email,
              clientName: formData.clientName,
              product: formData.product,
              contractType: formData.contractType,
              totalValue: formData.totalValue,
              monthlyValue: formData.monthlyValue,
              numParcels: formData.numParcels,
              startDate: formData.startDate,
            }),
          });
        } catch (emailError) {
          console.error('Welcome email error:', emailError);
        }

        toast.success('Contrato criado com sucesso!');
      }

      setFormData({ clientName: '', email: '', product: '', contractType: 'installment', totalValue: '', monthlyValue: '', numParcels: 12, startDate: '', status: 'active' });
      setEditingId(null);
      setShowNewContractModal(false);
      fetchContracts();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao salvar contrato');
    } finally {
      setLoading(prev => ({ ...prev, submitting: false }));
    }
  };

  const editContract = (contract) => {
    setFormData({
      clientName: contract.clientName,
      email: contract.email,
      product: contract.product,
      contractType: contract.contractType || 'installment',
      totalValue: contract.totalValue || '',
      monthlyValue: contract.monthlyValue || '',
      numParcels: contract.numParcels || 12,
      startDate: contract.startDate.toDate().toISOString().split('T')[0],
      status: contract.status || 'active'
    });
    setEditingId(contract.id);
    setShowNewContractModal(true);
  };

  const deleteContract = async (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Deletar Contrato',
      message: 'Tem certeza que deseja deletar este contrato? Esta acao nao pode ser desfeita.',
      confirmText: 'Deletar',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'contracts', id));
          await fetchContracts();
          toast.success('Contrato deletado com sucesso!');
        } catch (error) {
          console.error('Error:', error);
          toast.error('Erro ao deletar contrato');
        }
      }
    });
  };

  const cancelContract = (contract) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Cancelar Contrato',
      message: `Cancelar o contrato de ${contract.clientName}? As parcelas pendentes deixarao de ser cobradas e nenhum email sera enviado. O historico de pagamentos sera mantido e o contrato podera ser reativado depois.`,
      confirmText: 'Cancelar Contrato',
      variant: 'warning',
      onConfirm: async () => {
        try {
          await updateDoc(doc(db, 'contracts', contract.id), {
            status: 'cancelled',
            cancelledAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          });
          await fetchContracts();
          toast.success(`Contrato de ${contract.clientName} cancelado!`);
        } catch (error) {
          console.error('Error:', error);
          toast.error('Erro ao cancelar contrato');
        }
      }
    });
  };

  const reactivateContract = (contract) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Reativar Contrato',
      message: `Reativar o contrato de ${contract.clientName}? As parcelas pendentes voltarao a ser cobradas normalmente.`,
      confirmText: 'Reativar',
      variant: 'primary',
      onConfirm: async () => {
        try {
          await updateDoc(doc(db, 'contracts', contract.id), {
            status: 'active',
            cancelledAt: null,
            updatedAt: Timestamp.now(),
          });
          await fetchContracts();
          toast.success(`Contrato de ${contract.clientName} reativado!`);
        } catch (error) {
          console.error('Error:', error);
          toast.error('Erro ao reativar contrato');
        }
      }
    });
  };

  const togglePaymentStatus = async (contractId, parcelNum, currentStatus) => {
    try {
      const newStatus = currentStatus === 'paid' ? 'pending' : 'paid';
      const parcelRef = doc(db, 'contracts', contractId, 'parcels', parcelNum.toString());

      const updateData = { status: newStatus };
      if (newStatus === 'paid') {
        updateData.paidAt = Timestamp.now();
      }

      await updateDoc(parcelRef, updateData);
      await fetchContracts();
      toast.success(`Parcela ${parcelNum} marcada como ${newStatus === 'paid' ? 'paga' : 'pendente'}!`);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao atualizar status de pagamento');
    }
  };

  // Open contract detail view with parcels
  const openContractDetail = async (contract) => {
    setShowContractDetail(contract);
    try {
      const parcelsSnapshot = await getDocs(collection(db, 'contracts', contract.id, 'parcels'));
      const parcels = parcelsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      parcels.sort((a, b) => a.parcelNum - b.parcelNum);
      setContractParcels(parcels);
    } catch (error) {
      console.error('Error loading parcels:', error);
      toast.error('Erro ao carregar parcelas');
    }
  };

  // Modal helpers
  const openNewContractModal = () => {
    setEditingId(null);
    setFormData({ clientName: '', email: '', product: '', contractType: 'installment', totalValue: '', monthlyValue: '', numParcels: 12, startDate: '', status: 'active' });
    setShowNewContractModal(true);
  };

  const closeModal = () => {
    setShowNewContractModal(false);
    setEditingId(null);
    setFormData({ clientName: '', email: '', product: '', contractType: 'installment', totalValue: '', monthlyValue: '', numParcels: 12, startDate: '', status: 'active' });
  };

  // Export handlers
  const handleExportContracts = () => {
    exportContractsToCSV(contracts, emailsByContract);
    toast.success('Contratos exportados para CSV!');
  };

  const handleExportRevenue = () => {
    exportRevenueToCSV(revenueProjection);
    toast.success('Receitas exportadas para CSV!');
  };

  // Login screen
  if (!user) {
    return (
      <div style={styles.body}>
        <div style={styles.container}>
          <div style={{ maxWidth: '400px', margin: '100px auto', textAlign: 'center' }}>
            <h1 style={styles.title}>Email Admin</h1>
            <div style={{ ...styles.card, marginTop: '30px' }}>
              <div style={styles.form}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    style={styles.input}
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Senha</label>
                  <input
                    style={styles.input}
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Sua senha"
                    onKeyPress={e => e.key === 'Enter' && login()}
                  />
                </div>
                <button style={styles.button} onClick={login}>
                  Entrar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get paginated data
  const { contracts: paginatedContracts, totalItems, totalPages } = getPaginatedContracts();

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @media (max-width: 768px) {
            .responsive-grid { grid-template-columns: 1fr !important; }
            .responsive-flex { flex-direction: column !important; }
            .responsive-hide { display: none !important; }
          }
        `}
      </style>
      <div style={styles.body}>
        <div style={styles.container}>
          <header style={styles.header}>
            <h1 style={styles.title}>Email Admin</h1>
            <button style={{ ...styles.button, ...styles.buttonSecondary }} onClick={logout}>
              Sair
            </button>
          </header>

          {/* Tabs */}
          <div style={{ display: 'flex', marginBottom: '20px', flexWrap: 'wrap', gap: '4px' }}>
            {['dashboard', 'emails', 'templates', 'revenue', 'ltv'].map(tab => (
              <button
                key={tab}
                style={{ ...styles.tab, ...(currentTab === tab ? styles.tabActive : {}) }}
                onClick={() => setCurrentTab(tab)}
              >
                {tab === 'dashboard' ? 'Dashboard' :
                  tab === 'emails' ? `Emails (${scheduledEmails.length})` :
                    tab === 'templates' ? 'Templates' :
                      tab === 'revenue' ? 'Receitas' : 'LTV Clientes'}
              </button>
            ))}
          </div>

          {/* Dashboard Tab */}
          {currentTab === 'dashboard' && (
            <>
              {/* Stats */}
              <div style={styles.stats} className="responsive-grid">
                {loading.contracts ? (
                  <>
                    <StatCardSkeleton />
                    <StatCardSkeleton />
                    <StatCardSkeleton />
                    <StatCardSkeleton />
                  </>
                ) : (
                  <>
                    <div style={styles.statCard}>
                      <div style={styles.statValue}>{formatBRL(totalValue)}</div>
                      <div style={styles.statLabel}>Total em Contratos</div>
                    </div>
                    <div style={styles.statCard}>
                      <div style={styles.statValue}>
                        {contracts.filter(c => {
                          const s = getContractStatus(c);
                          return s === 'active' || s === 'overdue';
                        }).length}
                      </div>
                      <div style={styles.statLabel}>Contratos Ativos</div>
                    </div>
                    <div style={styles.statCard}>
                      <div style={styles.statValue}>
                        {formatBRL(Object.values(revenueForecast).reduce((a, b) => a + b, 0))}
                      </div>
                      <div style={styles.statLabel}>Receita Pendente</div>
                    </div>
                    <div style={styles.statCard}>
                      <div style={styles.statValue}>{formatBRL(mrr)}</div>
                      <div style={styles.statLabel}>MRR (Recorrente)</div>
                    </div>
                    <div style={styles.statCard}>
                      <div style={styles.statValue}>{scheduledEmails.length}</div>
                      <div style={styles.statLabel}>Emails Agendados</div>
                    </div>
                  </>
                )}
              </div>

              {/* Contracts Table */}
              <div style={styles.tableContainer}>
                <div style={styles.tableHeader}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                    <h2 style={{ color: '#f0f6fc', fontSize: '18px', margin: 0 }}>
                      Contratos ({totalItems})
                    </h2>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        style={{ ...styles.button, ...styles.buttonSecondary }}
                        onClick={handleExportContracts}
                      >
                        Exportar CSV
                      </button>
                      <button style={styles.headerButton} onClick={openNewContractModal}>
                        + Novo Contrato
                      </button>
                    </div>
                  </div>
                  <div style={styles.tableFilters} className="responsive-flex">
                    <input
                      type="text"
                      placeholder="Buscar por cliente, email ou produto..."
                      value={contractsSearch}
                      onChange={(e) => setContractsSearch(e.target.value)}
                      style={styles.searchInput}
                    />
                    <select
                      value={contractsFilter}
                      onChange={(e) => setContractsFilter(e.target.value)}
                      style={styles.select}
                    >
                      <option value="all">Todos os Status</option>
                      <option value="active">Ativos</option>
                      <option value="overdue">Em Atraso</option>
                      <option value="completed">Concluidos</option>
                      <option value="cancelled">Cancelados</option>
                      <option value="draft">Rascunhos</option>
                    </select>
                    <select
                      value={contractsSort}
                      onChange={(e) => setContractsSort(e.target.value)}
                      style={styles.select}
                    >
                      <option value="date-desc">Mais Recentes</option>
                      <option value="date-asc">Mais Antigos</option>
                      <option value="value-desc">Maior Valor</option>
                      <option value="value-asc">Menor Valor</option>
                      <option value="name-asc">Nome A-Z</option>
                    </select>
                  </div>
                </div>

                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.tableHeaderCell}>Cliente</th>
                      <th style={styles.tableHeaderCell}>Produto</th>
                      <th style={styles.tableHeaderCell}>Valor</th>
                      <th style={styles.tableHeaderCell} className="responsive-hide">Parcelas</th>
                      <th style={styles.tableHeaderCell}>Status</th>
                      <th style={styles.tableHeaderCell}>Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading.contracts ? (
                      <>
                        <TableRowSkeleton columns={6} />
                        <TableRowSkeleton columns={6} />
                        <TableRowSkeleton columns={6} />
                      </>
                    ) : (
                      paginatedContracts.map(contract => {
                        const status = getContractStatus(contract);
                        const stats = contractStats[contract.id] || { paid: 0, total: contract.numParcels };
                        const paidParcels = stats.paid;

                        return (
                          <tr key={contract.id} style={styles.tableRow}>
                            <td style={styles.tableCell}>
                              <div style={{ fontWeight: '600', color: '#58a6ff', cursor: 'pointer' }} onClick={() => openContractDetail(contract)}>{contract.clientName}</div>
                              <div style={{ fontSize: '12px', color: '#7d8590' }}>{contract.email}</div>
                            </td>
                            <td style={styles.tableCell}>{contract.product}</td>
                            <td style={styles.tableCell}>
                              {contract.contractType === 'recurring'
                                ? formatBRL(contract.monthlyValue) + '/mês'
                                : formatBRL(contract.totalValue)
                              }
                            </td>
                            <td style={styles.tableCell} className="responsive-hide">
                              {contract.contractType === 'recurring' ? (
                                <div style={{
                                  display: 'inline-block',
                                  padding: '4px 8px',
                                  backgroundColor: '#3fb950',
                                  color: '#fff',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}>
                                  Recorrente
                                </div>
                              ) : (
                                <>
                                  <div>{paidParcels}/{contract.numParcels}</div>
                                  <div style={{ fontSize: '12px', color: '#7d8590' }}>
                                    {formatBRL(contract.totalValue / contract.numParcels)} cada
                                  </div>
                                </>
                              )}
                            </td>
                            <td style={styles.tableCell}>
                              <StatusBadge status={status} />
                            </td>
                            <td style={styles.tableCell}>
                              <div style={styles.actionButtons}>
                                <button
                                  style={styles.actionButton}
                                  onClick={() => editContract(contract)}
                                >
                                  Editar
                                </button>
                                {status === 'cancelled' ? (
                                  <button
                                    style={{ ...styles.actionButton, color: '#3fb950' }}
                                    onClick={() => reactivateContract(contract)}
                                  >
                                    Reativar
                                  </button>
                                ) : (
                                  <button
                                    style={{ ...styles.actionButton, color: '#d29922' }}
                                    onClick={() => cancelContract(contract)}
                                  >
                                    Cancelar
                                  </button>
                                )}
                                <button
                                  style={{ ...styles.actionButton, color: '#da3633' }}
                                  onClick={() => deleteContract(contract.id)}
                                >
                                  Deletar
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>

                {!loading.contracts && totalItems === 0 && (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#7d8590' }}>
                    {debouncedSearch || contractsFilter !== 'all' ?
                      'Nenhum contrato encontrado com os filtros aplicados.' :
                      'Nenhum contrato encontrado. Crie seu primeiro contrato!'
                    }
                  </div>
                )}

                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={(newItemsPerPage) => {
                      setItemsPerPage(newItemsPerPage);
                      setCurrentPage(1);
                    }}
                  />
                )}
              </div>

              {/* Floating Action Button */}
              <button
                style={styles.fab}
                onClick={openNewContractModal}
                title="Criar Novo Contrato"
              >
                +
              </button>
            </>
          )}

          {/* Emails Tab */}
          {currentTab === 'emails' && (
            <div>
              {!selectedContract ? (
                <>
                  <h2 style={{ color: '#f0f6fc', fontSize: '18px', marginBottom: '20px' }}>
                    Contratos com Emails ({Object.keys(emailsByContract).length})
                  </h2>
                  {contracts.map(contract => {
                    const contractEmails = emailsByContract[contract.id] || [];
                    const pendingEmails = contractEmails.filter(e => e.status === 'scheduled').length;
                    const sentEmails = contractEmails.filter(e => e.status === 'sent').length;

                    return (
                      <div
                        key={contract.id}
                        style={{ ...styles.emailCard, cursor: 'pointer' }}
                        onClick={() => setSelectedContract(contract)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                          <div>
                            <div style={{ fontWeight: '600', color: '#58a6ff' }}>{contract.clientName}</div>
                            <div style={{ fontSize: '14px', color: '#7d8590' }}>{contract.email}</div>
                            <div style={{ fontSize: '14px' }}>{contract.product}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '14px', color: '#58a6ff' }}>{contractEmails.length} emails</div>
                            <div style={{ fontSize: '12px', color: '#7d8590' }}>
                              {sentEmails} enviados - {pendingEmails} agendados
                            </div>
                          </div>
                        </div>
                        <div style={{ fontSize: '13px', color: '#7d8590' }}>
                          Clique para ver todos os emails
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                    <button
                      style={{ ...styles.button, ...styles.buttonSecondary, marginRight: '16px' }}
                      onClick={() => setSelectedContract(null)}
                    >
                      Voltar
                    </button>
                    <div>
                      <h2 style={{ color: '#f0f6fc', fontSize: '18px', margin: 0 }}>
                        Emails - {selectedContract.clientName}
                      </h2>
                      <div style={{ fontSize: '14px', color: '#7d8590' }}>
                        {selectedContract.email} - {selectedContract.product}
                      </div>
                    </div>
                  </div>

                  {(emailsByContract[selectedContract.id] || []).map((emailItem, index) => (
                    <div key={index} style={styles.emailCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#58a6ff' }}>
                            {emailItem.type === 'welcome' ? 'Email de Boas-vindas' :
                              emailItem.type === 'reminder' ? 'Lembrete de Pagamento' :
                                emailItem.type === 'payment' ? 'Cobranca do Dia' : 'Follow-up de Atraso'}
                          </div>
                          <div style={{ fontSize: '14px', color: '#c9d1d9' }}>{emailItem.subject}</div>
                          {emailItem.parcelNum && (
                            <div style={{ fontSize: '13px', color: '#7d8590' }}>
                              Parcela {emailItem.parcelNum} - {formatBRL(emailItem.value)}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '12px', color: emailItem.status === 'sent' ? '#238636' : '#f85149' }}>
                            {emailItem.status === 'sent' ? 'Enviado' : 'Agendado'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#7d8590' }}>
                            {emailItem.scheduledDate.toLocaleDateString('pt-BR')}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <button
                          style={{ ...styles.button, ...styles.buttonSecondary }}
                          onClick={() => setShowEmailPreview({
                            email: emailItem,
                            subject: generateEmailSubject(emailItem, selectedContract),
                            recipient: selectedContract.email,
                            content: getIndividualEmailContent(emailItem, selectedContract)
                          })}
                        >
                          Visualizar
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Templates Tab */}
          {currentTab === 'templates' && (
            <div>
              <h2 style={{ color: '#f0f6fc', fontSize: '18px', marginBottom: '20px' }}>
                Templates de Email
              </h2>

              <TemplateVariablesHelp />

              <h3 style={{ color: '#f0f6fc', fontSize: '16px', marginBottom: '12px', marginTop: '8px', paddingBottom: '8px', borderBottom: '1px solid #30363d' }}>
                📄 Contratos Parcelados
              </h3>

              {['welcome', 'reminder', 'payment', 'followup'].map(type => {
                const template = emailTemplates[type];
                return (
                  <div key={type} style={styles.card}>
                    <h3 style={{ color: '#58a6ff', marginBottom: '16px' }}>
                      {type === 'welcome' ? 'Email de Boas-vindas' :
                        type === 'reminder' ? 'Email de Lembrete (7 dias antes)' :
                          type === 'payment' ? 'Email de Cobranca (no vencimento)' :
                            'Email de Follow-up (3 dias apos)'}
                    </h3>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ ...styles.label, display: 'block', marginBottom: '8px' }}>
                        Assunto:
                      </label>
                      <input
                        style={styles.input}
                        value={emailSubjects[type] || ''}
                        onChange={(e) => setEmailSubjects(prev => ({ ...prev, [type]: e.target.value }))}
                      />
                    </div>

                    <textarea
                      style={{
                        ...styles.input,
                        minHeight: '200px',
                        resize: 'vertical',
                        fontFamily: 'Monaco, monospace'
                      }}
                      value={template || ''}
                      onChange={(e) => setEmailTemplates(prev => ({ ...prev, [type]: e.target.value }))}
                    />

                    <div style={{ marginTop: '12px' }}>
                      <button style={styles.button} onClick={saveEmailTemplates}>
                        Salvar Template
                      </button>
                    </div>
                  </div>
                );
              })}

              <h3 style={{ color: '#f0f6fc', fontSize: '16px', marginBottom: '12px', marginTop: '32px', paddingBottom: '8px', borderBottom: '1px solid #30363d' }}>
                🔁 Contratos Recorrentes Mensais
              </h3>

              {['welcome_recurring', 'reminder_recurring', 'payment_recurring', 'followup_recurring'].map(type => {
                const template = emailTemplates[type];
                return (
                  <div key={type} style={styles.card}>
                    <h3 style={{ color: '#3fb950', marginBottom: '16px' }}>
                      {type === 'welcome_recurring' ? 'Email de Boas-vindas (Recorrente)' :
                        type === 'reminder_recurring' ? 'Email de Lembrete Recorrente (7 dias antes)' :
                          type === 'payment_recurring' ? 'Email de Cobrança Recorrente (no vencimento)' :
                            'Email de Follow-up Recorrente (3 dias após)'}
                    </h3>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ ...styles.label, display: 'block', marginBottom: '8px' }}>
                        Assunto:
                      </label>
                      <input
                        style={styles.input}
                        value={emailSubjects[type] || ''}
                        onChange={(e) => setEmailSubjects(prev => ({ ...prev, [type]: e.target.value }))}
                      />
                    </div>

                    <textarea
                      style={{
                        ...styles.input,
                        minHeight: '200px',
                        resize: 'vertical',
                        fontFamily: 'Monaco, monospace'
                      }}
                      value={template || ''}
                      onChange={(e) => setEmailTemplates(prev => ({ ...prev, [type]: e.target.value }))}
                    />

                    <div style={{ marginTop: '12px' }}>
                      <button style={styles.button} onClick={saveEmailTemplates}>
                        Salvar Template
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Revenue Tab */}
          {currentTab === 'revenue' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ color: '#f0f6fc', fontSize: '18px', margin: 0 }}>
                  Projecao de Receitas
                </h2>
                <button
                  style={{ ...styles.button, ...styles.buttonSecondary }}
                  onClick={handleExportRevenue}
                >
                  Exportar CSV
                </button>
              </div>

              {/* View toggle: expected (by due date) vs received (by paidAt) */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                <button
                  style={{ ...styles.tab, ...(revenueView === 'expected' ? styles.tabActive : {}) }}
                  onClick={() => setRevenueView('expected')}
                >
                  Previsto (por vencimento)
                </button>
                <button
                  style={{ ...styles.tab, ...(revenueView === 'received' ? styles.tabActive : {}) }}
                  onClick={() => setRevenueView('received')}
                >
                  Recebido (por pagamento)
                </button>
              </div>

              {revenueView === 'expected' && (
              <>
              <div style={styles.stats}>
                <div style={styles.statCard}>
                  <div style={styles.statValue}>
                    {Object.keys(revenueProjection).length}
                  </div>
                  <div style={styles.statLabel}>Meses com Receitas</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statValue}>
                    {formatBRL(Object.values(revenueProjection).reduce((sum, month) => sum + month.pending, 0))}
                  </div>
                  <div style={styles.statLabel}>Pendentes</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statValue}>
                    {formatBRL(Object.values(revenueProjection).reduce((sum, month) => sum + month.paid, 0))}
                  </div>
                  <div style={styles.statLabel}>Recebidas</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statValue}>
                    {formatBRL(Object.values(revenueProjection).reduce((sum, month) => sum + month.overdue, 0))}
                  </div>
                  <div style={styles.statLabel}>Em Atraso</div>
                </div>
              </div>

              {/* Revenue Forecast Chart */}
              {Object.keys(revenueProjection).length > 0 && (() => {
                const entries = Object.entries(revenueProjection);
                const maxTotal = Math.max(...entries.map(([, m]) => m.total), 1);

                // Get unique years and months for filtering
                const years = [...new Set(entries.map(([, m]) => {
                  const [month, year] = m.monthName.split(' de ');
                  return year;
                }))].sort((a, b) => b - a);

                return (
                  <div style={{ ...styles.revenueCard, marginBottom: '24px' }}>
                    <h3 style={{ color: '#f0f6fc', fontSize: '16px', margin: '0 0 20px 0' }}>
                      Previsao de Pagamento por Mes
                    </h3>

                    <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center' }}>
                      <select
                        value={selectedYear || ''}
                        onChange={(e) => {
                          setSelectedYear(e.target.value || null);
                          setSelectedMonth(null);
                        }}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#0d1117',
                          color: '#c9d1d9',
                          border: '1px solid #30363d',
                          borderRadius: '6px',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Todos os Anos</option>
                        {years.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>

                      {selectedYear && (
                        <select
                          value={selectedMonth || ''}
                          onChange={(e) => setSelectedMonth(e.target.value || null)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#0d1117',
                            color: '#c9d1d9',
                            border: '1px solid #30363d',
                            borderRadius: '6px',
                            fontSize: '14px',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="">Todos os Meses</option>
                          {entries
                            .filter(([, m]) => {
                              const [month, year] = m.monthName.split(' de ');
                              return year === selectedYear;
                            })
                            .map(([, m]) => {
                              const [month] = m.monthName.split(' de ');
                              return (
                                <option key={month} value={month}>
                                  {month.charAt(0).toUpperCase() + month.slice(1)}
                                </option>
                              );
                            })}
                        </select>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '200px', paddingBottom: '30px', position: 'relative' }}>
                      {entries.map(([monthKey, monthData]) => {
                        const barHeight = (monthData.total / maxTotal) * 160;
                        const paidH = monthData.total > 0 ? (monthData.paid / monthData.total) * barHeight : 0;
                        const pendingH = monthData.total > 0 ? (monthData.pending / monthData.total) * barHeight : 0;
                        const overdueH = monthData.total > 0 ? (monthData.overdue / monthData.total) * barHeight : 0;
                        const shortMonth = monthData.monthName.split(' ')[0].substring(0, 3);
                        const isHovered = hoveredBar === monthKey;
                        return (
                          <div key={monthKey} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '40px', position: 'relative' }}>
                            <div style={{ fontSize: '10px', color: '#c9d1d9', marginBottom: '4px', fontWeight: '600' }}>
                              {formatBRL(monthData.total)}
                            </div>
                            <div
                              style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '50px', height: `${barHeight}px`, cursor: 'pointer' }}
                              onMouseEnter={() => setHoveredBar(monthKey)}
                              onMouseLeave={() => setHoveredBar(null)}
                            >
                              {overdueH > 0 && <div style={{ height: `${overdueH}px`, backgroundColor: '#0EA5E9', borderRadius: '3px 3px 0 0' }} />}
                              {pendingH > 0 && <div style={{ height: `${pendingH}px`, backgroundColor: '#06B6D4', borderRadius: overdueH === 0 ? '3px 3px 0 0' : '0' }} />}
                              {paidH > 0 && <div style={{ height: `${paidH}px`, backgroundColor: '#4F46E5', borderRadius: '0 0 3px 3px' }} />}
                            </div>
                            {isHovered && (
                              <div style={{
                                position: 'absolute',
                                bottom: `${barHeight + 20}px`,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: '#0d1117',
                                border: '1px solid #30363d',
                                borderRadius: '6px',
                                padding: '8px 12px',
                                fontSize: '12px',
                                color: '#c9d1d9',
                                whiteSpace: 'nowrap',
                                zIndex: 10,
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)'
                              }}>
                                <div>Pendente: {formatBRL(monthData.pending)}</div>
                                {monthData.overdue > 0 && <div>Atrasado: {formatBRL(monthData.overdue)}</div>}
                              </div>
                            )}
                            <div style={{ fontSize: '10px', color: '#7d8590', marginTop: '6px', textTransform: 'capitalize' }}>{shortMonth}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '8px', fontSize: '12px' }}>
                      <span><span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#4F46E5', borderRadius: '2px', marginRight: '4px' }} />Pago</span>
                      <span><span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#06B6D4', borderRadius: '2px', marginRight: '4px' }} />Pendente</span>
                      <span><span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#0EA5E9', borderRadius: '2px', marginRight: '4px' }} />Atrasado</span>
                    </div>
                  </div>
                );
              })()}

              {Object.entries(revenueProjection)
                .filter(([, m]) => {
                  if (!selectedYear) return true;
                  const [month, year] = m.monthName.split(' de ');
                  return year === selectedYear && (!selectedMonth || month.toLowerCase() === selectedMonth.toLowerCase());
                })
                .map(([monthKey, monthData]) => {
                  const paidWidth = monthData.total > 0 ? (monthData.paid / monthData.total) * 100 : 0;
                  const pendingWidth = monthData.total > 0 ? (monthData.pending / monthData.total) * 100 : 0;
                  const overdueWidth = monthData.total > 0 ? (monthData.overdue / monthData.total) * 100 : 0;

                  return (
                  <div key={monthKey} style={styles.revenueCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div>
                        <h3 style={{ color: '#3B82F6', fontSize: '16px', margin: '0 0 8px 0' }}>
                          {monthData.monthName.charAt(0).toUpperCase() + monthData.monthName.slice(1)}
                        </h3>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#c9d1d9' }}>
                          {formatBRL(monthData.total)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', fontSize: '14px' }}>
                        <div style={{ color: '#4F46E5' }}>Pago: {formatBRL(monthData.paid)}</div>
                        <div style={{ color: '#06B6D4' }}>Pendente: {formatBRL(monthData.pending)}</div>
                        {monthData.overdue > 0 && (
                          <div style={{ color: '#0EA5E9' }}>Atrasado: {formatBRL(monthData.overdue)}</div>
                        )}
                      </div>
                    </div>

                    <div style={styles.revenueBar}>
                      <div style={{ display: 'flex', height: '100%' }}>
                        {monthData.paid > 0 && (
                          <div style={{ ...styles.revenueBarFill, backgroundColor: '#4F46E5', width: `${paidWidth}%` }} />
                        )}
                        {monthData.pending > 0 && (
                          <div style={{ ...styles.revenueBarFill, backgroundColor: '#06B6D4', width: `${pendingWidth}%` }} />
                        )}
                        {monthData.overdue > 0 && (
                          <div style={{ ...styles.revenueBarFill, backgroundColor: '#0EA5E9', width: `${overdueWidth}%` }} />
                        )}
                      </div>
                    </div>

                    <details style={{ marginTop: '16px' }}>
                      <summary style={{ cursor: 'pointer', color: '#3B82F6', fontSize: '14px' }}>
                        Ver {monthData.contracts.length} parcela{monthData.contracts.length !== 1 ? 's' : ''}
                      </summary>
                      <div style={{ marginTop: '12px' }}>
                        {monthData.contracts
                          .sort((a, b) => a.dueDate - b.dueDate)
                          .map((contract, index) => (
                            <div key={`${contract.id}-${contract.parcelNum}`} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '8px 0',
                              borderBottom: index < monthData.contracts.length - 1 ? '1px solid #30363d' : 'none',
                              fontSize: '13px'
                            }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ color: '#c9d1d9', fontWeight: '500' }}>{contract.clientName}</div>
                                <div style={{ color: '#7d8590' }}>{contract.product} - Parcela {contract.parcelNum}</div>
                              </div>
                              <div style={{ textAlign: 'center', margin: '0 16px' }}>
                                <div style={{ color: '#c9d1d9', fontWeight: '500' }}>{formatBRL(contract.value)}</div>
                                <div style={{ fontSize: '10px', color: '#7d8590' }}>
                                  {contract.dueDate.toLocaleDateString('pt-BR')}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <button
                                  style={{
                                    ...styles.button,
                                    padding: '6px 12px',
                                    fontSize: '12px',
                                    backgroundColor: contract.status === 'paid' ? '#4F46E5' :
                                      contract.dueDate < new Date() ? '#0EA5E9' : '#06B6D4',
                                  }}
                                  onClick={() => togglePaymentStatus(contract.id, contract.parcelNum, contract.status)}
                                >
                                  {contract.status === 'paid' ? 'Pago' :
                                    contract.dueDate < new Date() ? 'Atrasado' : 'Pendente'}
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    </details>
                  </div>
                );
              })}
              </>
              )}

              {revenueView === 'received' && (() => {
                const entries = Object.entries(receivedByMonth);
                const totalReceived = entries.reduce((s, [, m]) => s + m.received, 0);
                const totalOnTime = entries.reduce((s, [, m]) => s + m.onTime, 0);
                const totalLate = entries.reduce((s, [, m]) => s + m.late, 0);
                const onTimeRate = totalReceived > 0 ? Math.round((totalOnTime / totalReceived) * 100) : 0;
                const maxReceived = Math.max(...entries.map(([, m]) => m.received), 1);
                return (
                  <>
                    <div style={styles.stats}>
                      <div style={styles.statCard}>
                        <div style={styles.statValue}>{formatBRL(totalReceived)}</div>
                        <div style={styles.statLabel}>Total Recebido</div>
                      </div>
                      <div style={styles.statCard}>
                        <div style={styles.statValue}>{formatBRL(totalOnTime)}</div>
                        <div style={styles.statLabel}>Recebido no Prazo</div>
                      </div>
                      <div style={styles.statCard}>
                        <div style={styles.statValue}>{formatBRL(totalLate)}</div>
                        <div style={styles.statLabel}>Recebido em Atraso</div>
                      </div>
                      <div style={styles.statCard}>
                        <div style={styles.statValue}>{onTimeRate}%</div>
                        <div style={styles.statLabel}>Taxa de Pontualidade</div>
                      </div>
                    </div>

                    {entries.length === 0 ? (
                      <div style={{ padding: '40px', textAlign: 'center', color: '#7d8590' }}>
                        Nenhum pagamento registrado ainda. Marque parcelas como pagas para acompanhar o fluxo de caixa recebido.
                      </div>
                    ) : (
                      <div style={styles.revenueCard}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                          <h3 style={{ color: '#f0f6fc', fontSize: '16px', margin: 0 }}>
                            Recebido por Mes (data de pagamento)
                          </h3>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              style={{ ...styles.tab, ...(revenueTableView === 'chart' ? styles.tabActive : {}), padding: '8px 12px', fontSize: '12px' }}
                              onClick={() => setRevenueTableView('chart')}
                            >
                              Gráfico
                            </button>
                            <button
                              style={{ ...styles.tab, ...(revenueTableView === 'table' ? styles.tabActive : {}), padding: '8px 12px', fontSize: '12px' }}
                              onClick={() => setRevenueTableView('table')}
                            >
                              Tabela
                            </button>
                          </div>
                        </div>

                        {revenueTableView === 'chart' ? (
                          <>
                            <div style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: '10px' }}>
                              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '200px', paddingBottom: '30px', minWidth: 'min-content' }}>
                                {entries.map(([monthKey, m]) => {
                                  const barHeight = (m.received / maxReceived) * 160;
                                  const onTimeH = m.received > 0 ? (m.onTime / m.received) * barHeight : 0;
                                  const lateH = m.received > 0 ? (m.late / m.received) * barHeight : 0;
                                  const shortMonth = m.monthName.split(' ')[0].substring(0, 3);
                                  return (
                                    <div key={monthKey} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '60px', flexShrink: 0 }}>
                                      <div style={{ fontSize: '10px', color: '#c9d1d9', marginBottom: '4px', fontWeight: '600' }}>
                                        {formatBRL(m.received)}
                                      </div>
                                      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '50px', height: `${barHeight}px` }}>
                                        {lateH > 0 && <div style={{ height: `${lateH}px`, backgroundColor: '#d29922', borderRadius: '3px 3px 0 0' }} />}
                                        {onTimeH > 0 && <div style={{ height: `${onTimeH}px`, backgroundColor: '#238636', borderRadius: lateH === 0 ? '3px 3px 0 0' : '0' }} />}
                                      </div>
                                      <div style={{ fontSize: '10px', color: '#7d8590', marginTop: '6px', textTransform: 'capitalize' }}>{shortMonth}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '8px', fontSize: '12px' }}>
                              <span><span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#238636', borderRadius: '2px', marginRight: '4px' }} />No prazo</span>
                              <span><span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#d29922', borderRadius: '2px', marginRight: '4px' }} />Em atraso</span>
                            </div>
                          </>
                        ) : (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                              <thead>
                                <tr style={{ borderBottom: '2px solid #30363d' }}>
                                  <th style={{ padding: '12px', textAlign: 'left', color: '#c9d1d9', fontWeight: '600' }}>Mês</th>
                                  <th style={{ padding: '12px', textAlign: 'right', color: '#c9d1d9', fontWeight: '600' }}>Recebido</th>
                                  <th style={{ padding: '12px', textAlign: 'right', color: '#c9d1d9', fontWeight: '600' }}>No Prazo</th>
                                  <th style={{ padding: '12px', textAlign: 'right', color: '#c9d1d9', fontWeight: '600' }}>Em Atraso</th>
                                  <th style={{ padding: '12px', textAlign: 'right', color: '#c9d1d9', fontWeight: '600' }}>Taxa Coleta</th>
                                </tr>
                              </thead>
                              <tbody>
                                {entries.map(([monthKey, m], index) => {
                                  const collectionRate = m.received > 0 ? Math.round((m.onTime / m.received) * 100) : 0;
                                  return (
                                    <tr key={monthKey} style={{ borderBottom: index < entries.length - 1 ? '1px solid #30363d' : 'none' }}>
                                      <td style={{ padding: '12px', color: '#c9d1d9' }}>{m.monthName}</td>
                                      <td style={{ padding: '12px', textAlign: 'right', color: '#c9d1d9', fontWeight: '500' }}>{formatBRL(m.received)}</td>
                                      <td style={{ padding: '12px', textAlign: 'right', color: '#238636' }}>{formatBRL(m.onTime)}</td>
                                      <td style={{ padding: '12px', textAlign: 'right', color: '#d29922' }}>{formatBRL(m.late)}</td>
                                      <td style={{ padding: '12px', textAlign: 'right', color: collectionRate >= 90 ? '#238636' : collectionRate >= 70 ? '#d29922' : '#f85149', fontWeight: '500' }}>
                                        {collectionRate}%
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Client LTV Tab */}
          {currentTab === 'ltv' && (() => {
            const clientLTV = {};
            contracts.forEach(contract => {
              const parcels = parcelsByContract[contract.id] || [];
              const isCancelled = contract.status === 'cancelled';
              const received = parcels.filter(p => p.status === 'paid').reduce((s, p) => s + p.value, 0);
              // Cancelled contracts: only the realized value counts (unpaid parcels are no longer owed)
              const contractValue = isCancelled
                ? received
                : (contract.contractType === 'recurring' ? contract.monthlyValue * 12 : contract.totalValue);

              if (!clientLTV[contract.clientName]) {
                clientLTV[contract.clientName] = { contracted: 0, received: 0, numContracts: 0, parcels: [] };
              }
              clientLTV[contract.clientName].contracted += contractValue;
              clientLTV[contract.clientName].received += received;
              clientLTV[contract.clientName].numContracts += 1;
              clientLTV[contract.clientName].parcels.push(...(isCancelled ? parcels.filter(p => p.status === 'paid') : parcels));
            });

            const clientRows = Object.entries(clientLTV).map(([clientName, data]) => {
              const collectionRate = data.contracted > 0 ? Math.round((data.received / data.contracted) * 100) : 0;
              const outstanding = data.contracted - data.received;
              const unpaidParcels = data.parcels.filter(p => p.status !== 'paid');
              const today = new Date();
              const daysOverdue = unpaidParcels.length > 0
                ? Math.max(...unpaidParcels.map(p => {
                  const dueDate = new Date(p.dueDate);
                  const days = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
                  return Math.max(0, days);
                }))
                : null;
              return { clientName, contracted: data.contracted, received: data.received, outstanding, collectionRate, daysOverdue, numContracts: data.numContracts };
            }).sort((a, b) => (b.daysOverdue || 0) - (a.daysOverdue || 0));

            return (
              <div>
                <h2 style={{ color: '#f0f6fc', fontSize: '20px', marginBottom: '20px' }}>Valor Vitalício do Cliente</h2>
                {clientRows.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#7d8590' }}>
                    Nenhum cliente cadastrado ainda.
                  </div>
                ) : (
                  <div style={styles.tableContainer}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.tableHeader}>Cliente</th>
                          <th style={styles.tableHeader}>Contratos</th>
                          <th style={styles.tableHeader}>Contratado</th>
                          <th style={styles.tableHeader}>Recebido</th>
                          <th style={styles.tableHeader}>Pendente</th>
                          <th style={styles.tableHeader}>Taxa Coleta</th>
                          <th style={styles.tableHeader}>Dias em Atraso</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientRows.map((row, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #30363d' }}>
                            <td style={styles.tableCell}>{row.clientName}</td>
                            <td style={styles.tableCell}>{row.numContracts}</td>
                            <td style={styles.tableCell}>{formatBRL(row.contracted)}</td>
                            <td style={styles.tableCell}>{formatBRL(row.received)}</td>
                            <td style={styles.tableCell}>{formatBRL(row.outstanding)}</td>
                            <td style={styles.tableCell}>
                              <span style={{
                                color: row.collectionRate >= 90 ? '#3fb950' : row.collectionRate >= 70 ? '#d29922' : '#f85149',
                                fontWeight: '600'
                              }}>
                                {row.collectionRate}%
                              </span>
                            </td>
                            <td style={styles.tableCell}>
                              {row.daysOverdue === null ? (
                                <span style={{ color: '#3fb950' }}>—</span>
                              ) : row.daysOverdue === 0 ? (
                                <span style={{ color: '#3fb950' }}>No prazo</span>
                              ) : (
                                <span style={{ color: '#f85149', fontWeight: '600' }}>{row.daysOverdue}+ dias</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}

          <div style={{ fontSize: '12px', color: '#7d8590', marginTop: '30px', textAlign: 'center' }}>
            LGPD: Ao usar esta aplicacao, voce consente com o armazenamento seguro de dados para fins de cobranca.
          </div>
        </div>
      </div>

      {/* Contract Modal */}
      <Modal
        isOpen={showNewContractModal}
        onClose={closeModal}
        title={editingId ? 'Editar Contrato' : 'Novo Contrato'}
      >
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Nome do Cliente</label>
            <input
              style={styles.input}
              value={formData.clientName}
              onChange={e => setFormData({ ...formData, clientName: e.target.value })}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              style={styles.input}
              type="email"
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Produto/Servico</label>
            <input
              style={styles.input}
              value={formData.product}
              onChange={e => setFormData({ ...formData, product: e.target.value })}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Tipo de Contrato</label>
            <select
              style={styles.select}
              value={formData.contractType}
              onChange={e => setFormData({ ...formData, contractType: e.target.value })}
            >
              <option value="installment">Parcelado</option>
              <option value="recurring">Recorrente Mensal</option>
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {formData.contractType === 'installment' ? (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Valor Total (R$)</label>
                  <input
                    style={styles.input}
                    type="number"
                    step="0.01"
                    value={formData.totalValue}
                    onChange={e => setFormData({ ...formData, totalValue: parseFloat(e.target.value) })}
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Parcelas</label>
                  <input
                    style={styles.input}
                    type="number"
                    min="1"
                    value={formData.numParcels}
                    onChange={e => setFormData({ ...formData, numParcels: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </>
            ) : (
              <div style={styles.formGroup}>
                <label style={styles.label}>Valor Mensal (R$)</label>
                <input
                  style={styles.input}
                  type="number"
                  step="0.01"
                  value={formData.monthlyValue}
                  onChange={e => setFormData({ ...formData, monthlyValue: parseFloat(e.target.value) })}
                  required
                />
              </div>
            )}
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Data da Primeira Parcela</label>
            <input
              style={styles.dateInput}
              type="date"
              value={formData.startDate}
              onChange={e => setFormData({ ...formData, startDate: e.target.value })}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Status do Contrato</label>
            <select
              style={styles.select}
              value={formData.status}
              onChange={e => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="active">Ativo</option>
              <option value="draft">Rascunho</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button
              type="button"
              style={{ ...styles.button, ...styles.buttonSecondary }}
              onClick={closeModal}
            >
              Cancelar
            </button>
            <button
              style={{
                ...styles.button,
                backgroundColor: loading.submitting ? '#6b7280' : '#238636',
                cursor: loading.submitting ? 'not-allowed' : 'pointer'
              }}
              type="submit"
              disabled={loading.submitting}
            >
              {loading.submitting ? 'Salvando...' : (editingId ? 'Atualizar' : 'Criar') + ' Contrato'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText || 'Confirmar'}
        variant={confirmDialog.variant || 'danger'}
      />

      {/* Contract Detail Modal */}
      {showContractDetail && (
        <Modal
          isOpen={true}
          onClose={() => { setShowContractDetail(null); setContractParcels([]); }}
          title={showContractDetail.clientName}
          maxWidth="700px"
        >
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#7d8590' }}>Email</div>
                <div style={{ color: '#c9d1d9' }}>{showContractDetail.email}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#7d8590' }}>Produto</div>
                <div style={{ color: '#c9d1d9' }}>{showContractDetail.product}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#7d8590' }}>Valor Total</div>
                <div style={{ color: '#58a6ff', fontWeight: '600' }}>{formatBRL(showContractDetail.totalValue)}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#7d8590' }}>Parcelas</div>
                <div style={{ color: '#c9d1d9' }}>{showContractDetail.numParcels}x de {formatBRL(showContractDetail.totalValue / showContractDetail.numParcels)}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#7d8590' }}>Status</div>
                <StatusBadge status={getContractStatus(showContractDetail)} />
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#7d8590' }}>Inicio</div>
                <div style={{ color: '#c9d1d9' }}>{showContractDetail.startDate?.toDate().toLocaleDateString('pt-BR')}</div>
              </div>
              {showContractDetail.status === 'cancelled' && showContractDetail.cancelledAt && (
                <div>
                  <div style={{ fontSize: '12px', color: '#7d8590' }}>Cancelado em</div>
                  <div style={{ color: '#f85149' }}>{showContractDetail.cancelledAt.toDate().toLocaleDateString('pt-BR')}</div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              <button
                style={styles.button}
                onClick={() => { setShowContractDetail(null); setContractParcels([]); editContract(showContractDetail); }}
              >
                Editar Contrato
              </button>
              {showContractDetail.status === 'cancelled' ? (
                <button
                  style={{ ...styles.button, backgroundColor: '#238636' }}
                  onClick={() => { setShowContractDetail(null); setContractParcels([]); reactivateContract(showContractDetail); }}
                >
                  Reativar Contrato
                </button>
              ) : (
                <button
                  style={{ ...styles.button, backgroundColor: '#9e6a03' }}
                  onClick={() => { setShowContractDetail(null); setContractParcels([]); cancelContract(showContractDetail); }}
                >
                  Cancelar Contrato
                </button>
              )}
            </div>

            <h3 style={{ color: '#f0f6fc', fontSize: '16px', margin: '0 0 12px 0', paddingTop: '16px', borderTop: '1px solid #30363d' }}>
              Parcelas ({contractParcels.length})
            </h3>

            {contractParcels.length === 0 ? (
              <div style={{ color: '#7d8590', textAlign: 'center', padding: '20px' }}>Carregando parcelas...</div>
            ) : (
              <div>
                {contractParcels.map((parcel) => {
                  const dueDate = parcel.dueDate?.toDate();
                  const isOverdue = parcel.status === 'pending' && dueDate && dueDate < new Date();
                  const isPaid = parcel.status === 'paid';
                  return (
                    <div key={parcel.parcelNum} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 0',
                      borderBottom: '1px solid #30363d',
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#c9d1d9', fontWeight: '500' }}>
                          Parcela {parcel.parcelNum}/{showContractDetail.numParcels}
                        </div>
                        <div style={{ fontSize: '12px', color: '#7d8590' }}>
                          Vencimento: {dueDate?.toLocaleDateString('pt-BR')}
                          {parcel.paidAt && ` | Pago em: ${parcel.paidAt.toDate().toLocaleDateString('pt-BR')}`}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center', margin: '0 16px' }}>
                        <div style={{ color: '#c9d1d9', fontWeight: '600' }}>{formatBRL(parcel.value)}</div>
                      </div>
                      <button
                        style={{
                          ...styles.button,
                          padding: '6px 14px',
                          fontSize: '12px',
                          backgroundColor: isPaid ? '#238636' : isOverdue ? '#da3633' : '#f85149',
                        }}
                        onClick={async () => {
                          await togglePaymentStatus(showContractDetail.id, parcel.parcelNum, parcel.status);
                          openContractDetail(showContractDetail);
                        }}
                      >
                        {isPaid ? 'Pago' : isOverdue ? 'Atrasado' : 'Pendente'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Email Preview Modal */}
      {showEmailPreview && (
        <EmailPreview
          isOpen={true}
          onClose={() => setShowEmailPreview(null)}
          email={showEmailPreview.email}
          subject={showEmailPreview.subject}
          recipient={showEmailPreview.recipient}
          content={showEmailPreview.content}
        />
      )}
    </>
  );
}

export default App;
